import { CalendarDays, Paperclip, Users } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { DateValue, EmptyState, StatusBadge } from '@/components/common'
import { downloadFile } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import type { LeaveDecisionContext } from '@/features/leave'

/**
 * The four things that decide a leave request.
 *
 * <p>The balance first, because approving thirty days against an entitlement of fifteen is
 * the commonest mistake and it is invisible from the request alone.
 */
export function LeaveContext({ detail }: { detail: LeaveDecisionContext }) {
  const balance = detail.balanceForThisType
  const wouldOverdraw = balance != null && detail.request.days > balance.available

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">Balance</h3>
        {balance == null ? (
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            No balance is held for {detail.request.leaveTypeName} — an unpaid or non-accruing
            type, or a first request this year.
          </p>
        ) : (
          <>
            <dl className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {[
                ['Entitled', balance.entitled],
                ['Used', balance.used],
                ['Held', balance.pending],
                ['Available', balance.available],
              ].map(([label, value]) => (
                <div key={label as string}>
                  <dt className="text-xs text-[var(--color-muted)]">{label}</dt>
                  <dd className="tabular-nums text-[var(--color-heading)]">{value as number}</dd>
                </div>
              ))}
            </dl>
            {wouldOverdraw && (
              <p role="alert" className="mt-2 text-sm font-medium text-[var(--color-danger)]">
                This request is {detail.request.days} days against {balance.available} available.
              </p>
            )}
          </>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <Users className="size-4" aria-hidden="true" />
          Who else is away then
        </h3>
        {detail.teamOverlap.length === 0 ? (
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            Nobody else in the same department, as the team stood on the start date.
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm">
            {detail.teamOverlap.map((row) => (
              <li key={`${row.employeeId}-${row.fromDate}`} className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--color-heading)]">{row.employeeName}</span>
                <span className="text-[var(--color-muted)]">
                  <DateValue value={row.fromDate} /> – <DateValue value={row.toDate} />
                </span>
                <StatusBadge status={row.status} />
                <span className="text-xs text-[var(--color-muted)]">
                  {row.overlappingDays} {row.overlappingDays === 1 ? 'day' : 'days'} overlap
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <CalendarDays className="size-4" aria-hidden="true" />
          Their last {detail.recentRequests.length || 'few'} requests
        </h3>
        {detail.recentRequests.length === 0 ? (
          <EmptyState title="Nothing before this" description="Their first request of any kind." />
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm">
            {detail.recentRequests.map((request) => (
              <li key={request.id} className="flex flex-wrap items-center gap-2">
                <span className="text-[var(--color-heading)]">{request.leaveTypeName}</span>
                <span className="text-[var(--color-muted)]">
                  <DateValue value={request.fromDate} /> – <DateValue value={request.toDate} />
                </span>
                <span className="tabular-nums text-[var(--color-muted)]">{request.days} d</span>
                <StatusBadge status={request.status} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {detail.documentFileId && (
        <Card className="flex items-center justify-between gap-3 p-4">
          <span className="flex items-center gap-2 text-sm text-[var(--color-heading)]">
            <Paperclip className="size-4" aria-hidden="true" />
            A document was attached — a certificate nobody looks at is a policy nobody enforces.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void downloadFile(`/v1/files/${detail.documentFileId}`, 'leave-document')}
          >
            Open it
          </Button>
        </Card>
      )}
    </div>
  )
}
